import * as PIXI from 'pixi.js';
import { removeItem } from './array-utils';
import { resetSprite } from './display-utils';
import { Signal } from 'framework/utils/Signal';

export interface IPoolDefaultValues {
  parent?: PIXI.Container | null;
  anchor?: number;
  scale?: number;
  alpha?: number,
}

export class PoolGroup<T extends PIXI.DisplayObject> {
  public signals = {
    onRelease: new Signal<T>(),
  };

  private inactiveInstances: T[] = [];
  private activeInstances: T[] = [];
  private readonly createFunction: () => T;
  private readonly resetFunction: (elem: T) => void;
  private readonly destroyFunction: (elem: T) => void;

  public static createSpritePool(defaultValues: IPoolDefaultValues = {}): PoolGroup<PIXI.Sprite> {
    const defValues = {
      parent: null,
      scale: 1,
      anchor: 1,
      alpha: 1,
      ...defaultValues,
    };

    return new PoolGroup(
      () => new PIXI.Sprite(),
      (sprite) => {
        resetSprite(sprite);
        sprite.anchor.set(defValues.anchor);
        sprite.scale.set(defValues.scale);
        sprite.alpha = defValues.alpha;
        if (defValues.parent) defValues.parent.addChild(sprite);
      },
      (sprite) => sprite.destroy({ children: true }),
    );
  }

  constructor(createFunction: () => T, resetFunction: (elem: T) => void, destroyFunction: (elem: T) => void) {
    this.createFunction = createFunction;
    this.resetFunction = resetFunction;
    this.destroyFunction = destroyFunction;
  }

  public getInstance(): T {
    const instance = this.inactiveInstances.pop() || this.createFunction();
    this.activeInstances.push(instance);
    this.resetFunction(instance);
    return instance;
  }

  public release(instance: T): void {
    instance.parent && instance.parent.removeChild(instance);
    removeItem(this.activeInstances, instance);
    this.inactiveInstances.push(instance);
    instance.onReleased.dispatch();
    this.signals.onRelease.dispatch(instance);
  }

  public destroy(): void {
    [...this.activeInstances, ...this.inactiveInstances].forEach((instance) => this.destroyFunction(instance));
  }
}
